import { useState, useRef, useEffect, useCallback } from 'react'
import Slider from 'react-slick'
import { SlideOnMount } from '../../../../shared/Animations'
import Carousel from '../../../../shared/Carousel'
import { PreviousArrow } from '../PreviousArrow/PreviousArrow'
import { NextArrow } from '../NextArrow/NextArrow'
import { ClientEventName, Listing } from '../../../../../api/gqlaxy/gql/generated/graphql'
import { KEY_CODES } from '../../../../../constants/utility'
import { LISTING_PHOTOS_CONTENT_PANEL } from '../../../../../constants/listings'
import { Card, Text } from '@homebotapp/hb-react-component-catalog'

import styles from './ListingDetailsPhotos.module.scss'
import { HOME_SEARCH_LISTING_DETAILS } from '../../../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import { useCreateClientEvent, useEffectOncePerSessionConditionally } from '../../../../../hooks'

export interface ListingDetailsPhotosProps {
  listing: Listing
  slidesToShow?: number
  slidesToScroll?: number
  className?: string
}

export const ListingDetailsPhotos = ({ listing, slidesToShow = 1, slidesToScroll = 1 }: ListingDetailsPhotosProps) => {
  const [imageIndex, setImageIndex] = useState(0)
  const [carouselInteracted, setCarouselInteracted] = useState(false)
  const sliderRef = useRef<Slider>(null)

  const next = () => sliderRef.current?.slickNext()
  const previous = () => sliderRef.current?.slickPrev()
  const reset = () => sliderRef.current?.slickGoTo(0)

  const { mutate: createClientEvent } = useCreateClientEvent({ disableErrorPrompts: true })

  const sessionStorageCarouselKey = ClientEventName.ListingPhotoInteraction

  const createCarouselClientEvent = useCallback(() => {
    createClientEvent({
      clientEventInput: {
        eventName: ClientEventName.ListingPhotoInteraction,
        listingId: listing.id
      }
    })
  }, [createClientEvent])

  useEffectOncePerSessionConditionally(
    createCarouselClientEvent,
    sessionStorageCarouselKey,
    Boolean(carouselInteracted)
  )

  const handleArrowKeydown = useCallback(e => {
    if ((e.which || e.keyCode) === KEY_CODES.rightArrow) {
      next()
    } else if ((e.which || e.keyCode) === KEY_CODES.leftArrow) {
      previous()
    }
  }, [])

  useEffect(() => {
    setImageIndex(0)
    setTimeout(() => {
      reset()
    }, 500)
  }, [listing])

  useEffect(() => {
    document.addEventListener('keydown', handleArrowKeydown)

    return () => {
      document.removeEventListener('keydown', handleArrowKeydown)
    }
  }, [])

  const generateListingSlides = () =>
    listing?.thumbnailImageFileNames?.map(image => image && <img src={image} className={styles.image} alt='listing' />)

  const changeImage = ({ current }: { current: number }) => {
    if (!carouselInteracted) {
      setCarouselInteracted(true)
    }

    setImageIndex(current)
  }

  const slides = generateListingSlides()

  return (
    <SlideOnMount interval={50}>
      <div id={LISTING_PHOTOS_CONTENT_PANEL} className={styles.content}>
        <Carousel
          afterChange={current => changeImage({ current })}
          arrows
          infinite
          draggable
          initialSlide={0}
          nextArrow={<NextArrow />}
          prevArrow={<PreviousArrow />}
          sliderRef={sliderRef}
          slides={slides}
          slidesToScroll={slidesToScroll}
          slidesToShow={slidesToShow}
          speed={300}
          swipeToSlide
          tracking={{
            guid: 'ikViZ5Dmz7tfxxsvsjrJU3',
            ui_context: HOME_SEARCH_LISTING_DETAILS,
            descriptor: 'photo carousel'
          }}
        />
        {listing?.thumbnailImageFileNames?.length && (
          <Card size='lg' bottom={4} left='50%' paddingX={2} position='absolute' transform='translate(-50%, 0)'>
            <Text as='span' size='2xs'>
              {`${imageIndex + 1} / ${listing.thumbnailImageFileNames.length}`}
            </Text>
          </Card>
        )}
      </div>
    </SlideOnMount>
  )
}
